import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Share2, Copy, MessageCircle, Send, Check, Facebook, Twitter } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';

export const ShareButton = ({ url, title, description, className }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { t } = useLanguage();
  const fullUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const shareText = `${title || ''}${description ? `\n${description}` : ''}\n${fullUrl}`;

  const tryNativeShare = async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title, text: description, url: fullUrl });
        return true;
      } catch {
        // user cancelled — fall back to dialog
      }
    }
    return false;
  };

  const handleClick = async () => {
    const shared = await tryNativeShare();
    if (!shared) setOpen(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success(t('share.copied', 'Link copied!'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy');
    }
  };

  const whatsappLink = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const fbLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}`;
  const twitterLink = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title || '')}&url=${encodeURIComponent(fullUrl)}`;
  const telegramLink = `https://t.me/share/url?url=${encodeURIComponent(fullUrl)}&text=${encodeURIComponent(title || '')}`;

  return (
    <>
      <Button
        variant="outline"
        type="button"
        onClick={handleClick}
        className={className}
        data-testid="share-property-btn"
      >
        <Share2 className="h-4 w-4 mr-2" />
        {t('cta.share', 'Share')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('share.title', 'Share this property')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2 p-2 border rounded-lg bg-gray-50">
              <input
                readOnly
                value={fullUrl}
                className="flex-1 bg-transparent text-sm outline-none truncate"
              />
              <Button size="sm" variant="outline" onClick={copy} data-testid="copy-link-btn">
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp-btn">
              <Button className="w-full bg-green-600 hover:bg-green-700 text-white">
                <MessageCircle className="h-4 w-4 mr-2" /> {t('share.whatsapp', 'Share on WhatsApp')}
              </Button>
            </a>
            <div className="grid grid-cols-3 gap-2">
              <a href={fbLink} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full"><Facebook className="h-4 w-4" /></Button>
              </a>
              <a href={twitterLink} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full"><Twitter className="h-4 w-4" /></Button>
              </a>
              <a href={telegramLink} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full"><Send className="h-4 w-4" /></Button>
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
